/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { isChrome } from 'react-device-detect';
import { getSearchHitsByPage, magazine } from './common/ducks';
import { getDevicePixelRatio } from './common/utils';
import { useMuPDF, renderPageToCanvas } from './common/mupdf-context';

/**
 * Page component — renders PDF pages using MuPDF WASM.
 *
 * MuPDF's C engine handles complex vector content (gradient fills, soft masks)
 * 5-10× faster than PDF.js's JavaScript implementation. Rendering is synchronous
 * in WASM, so there are no async cancel/restart issues.
 *
 * Two-pass rendering:
 *   Pass 1 (fast): pixelRatio=1 → instant preview
 *   Pass 2 (quality): pixelRatio=devicePixelRatio → crisp on Retina
 */

const MAX_PAGE_SIZE = 4095;

function Page({
  pageNumber,
  turned,
  left,
  onRatioChange,
  onRenderComplete,
  height,
  width,
  maxWidth,
  renderMode = 'canvas',
  scale = 1,
  enhance = false,
  // Redux
  onePager,
  isSearchMode,
  searchHitsByPage,
  backwards,
  dispatch,
}) {
  const canvasRef = useRef(null);
  const lastRenderKeyRef = useRef(null);
  const pageDimsRef = useRef({ width: 0, height: 0 });
  const [enhancedAvailable, setEnhancedAvailable] = useState(false);
  const [links, setLinks] = useState([]);

  // Stable callback refs — prevent effect restarts when parent re-renders
  const onRatioChangeRef = useRef(onRatioChange);
  onRatioChangeRef.current = onRatioChange;
  const onRenderCompleteRef = useRef(onRenderComplete);
  onRenderCompleteRef.current = onRenderComplete;

  // Get the loaded MuPDF document from context
  const { doc } = useMuPDF() || {};

  // Effect 1: Report page aspect ratio (synchronous, very fast — no render).
  useLayoutEffect(() => {
    if (!doc) return;
    try {
      const page = doc.loadPage(pageNumber - 1);
      const bounds = page.getBounds();
      const pw = bounds[2] - bounds[0];
      const ph = bounds[3] - bounds[1];
      pageDimsRef.current = { width: pw, height: ph };
      page.destroy();
      if (onRatioChangeRef.current) {
        onRatioChangeRef.current(pageNumber, pw / ph);
      }
    } catch (err) {
      console.error(`[Page ${pageNumber}] ratio error:`, err);
    }
  }, [doc, pageNumber]);

  // Effect: Extract link annotations from the PDF page.
  useEffect(() => {
    if (!doc) { setLinks([]); return; }
    try {
      const page = doc.loadPage(pageNumber - 1);
      const pageLinks = page.getLinks();
      const extracted = pageLinks.map(link => {
        const bounds = link.getBounds();
        const uri = link.getURI();
        const isExternal = link.isExternal();
        let targetPage = null;
        if (!isExternal) {
          targetPage = doc.resolveLink(link);
        }
        return { bounds, uri, isExternal, targetPage };
      });
      page.destroy();
      setLinks(extracted);
    } catch (err) {
      console.error(`[Page ${pageNumber}] link extraction error:`, err);
      setLinks([]);
    }
  }, [doc, pageNumber]);

  const handleLinkClick = (e, link) => {
    e.preventDefault();
    e.stopPropagation();
    if (link.isExternal) {
      window.open(link.uri, '_blank', 'noopener,noreferrer');
    } else if (link.targetPage != null) {
      dispatch(magazine.setPageNumber(link.targetPage + 1)); // MuPDF is 0-based, Redux is 1-based
    }
  };

  // Clear canvas immediately on page change so the old page content
  // doesn't show through while the new render is pending.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    lastRenderKeyRef.current = null;
  }, [pageNumber]);

  // Effect 1b: Render pass 1 — deferred to rAF so the page-turn animation
  // can start immediately without the main thread freezing.
  useEffect(() => {
    if (!doc || !canvasRef.current || !height || height <= 0) return;

    const stableHeight = Math.round(height / 5) * 5;
    const renderKey = `${pageNumber}-${stableHeight}`;
    if (renderKey === lastRenderKeyRef.current) return;

    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (cancelled || !canvasRef.current) return;
      try {
        renderPageToCanvas(doc, pageNumber - 1, canvasRef.current, height, 1);
        lastRenderKeyRef.current = renderKey;
        if (onRenderCompleteRef.current) onRenderCompleteRef.current(pageNumber);
      } catch (err) {
        console.error(`[Page ${pageNumber}] render error:`, err);
      }
    });

    return () => { cancelled = true; cancelAnimationFrame(id); };
  }, [doc, pageNumber, height]);

  // Effect 2: Deferred HiDPI pass — runs after paint so it doesn't block UI.
  useEffect(() => {
    if (!doc || !canvasRef.current || !height || height <= 0) return;
    const dpr = getDevicePixelRatio();
    if (dpr <= 1) return;

    let cancelled = false;
    const schedule = window.requestIdleCallback || window.requestAnimationFrame;
    const id = schedule(() => {
      if (cancelled || !canvasRef.current) return;
      try {
        renderPageToCanvas(doc, pageNumber - 1, canvasRef.current, height, dpr);
      } catch (err) {
        console.error(`[Page ${pageNumber}] pass2 error:`, err);
      }
    });

    return () => {
      cancelled = true;
      const cancel = window.cancelIdleCallback || window.cancelAnimationFrame;
      cancel(id);
    };
  }, [doc, pageNumber, height]);

  // Enhanced (high-res) canvas for zoom
  const enhancedCanvasRef = useRef(null);

  let enhancedScale = scale >= 2.5 ? 4 : scale >= 1.25 ? 2 : 1;
  const { height: pgH, width: pgW } = pageDimsRef.current;
  const devicePR = getDevicePixelRatio();
  if (pgH > 0 && pgW > 0) {
    enhancedScale = Math.min(
      enhancedScale,
      Math.min(
        MAX_PAGE_SIZE / pgH / devicePR,
        MAX_PAGE_SIZE / pgW / devicePR
      )
    );
  }

  const [enhancedRendering, setEnhancedRendering] = useState(false);

  useEffect(() => {
    if (!enhance || enhancedScale <= 1 || !doc || !enhancedCanvasRef.current || !height) {
      setEnhancedRendering(false);
      return;
    }

    setEnhancedAvailable(false);
    setEnhancedRendering(true);

    // Defer heavy WASM render so the spinner has time to appear
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled || !enhancedCanvasRef.current) return;
      try {
        const enhancedHeight = height * enhancedScale;
        const pr = getDevicePixelRatio();
        renderPageToCanvas(doc, pageNumber - 1, enhancedCanvasRef.current, enhancedHeight, pr);
        setEnhancedAvailable(true);
      } catch (err) {
        console.error(`[Page ${pageNumber}] enhanced render error:`, err);
      }
      setEnhancedRendering(false);
    }, 50);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [doc, pageNumber, height, enhance, enhancedScale]);

  return (
    <PageWrapper
      className={`page-wrapper ${turned ? 'turned' : ''}`}
      $pageNumber={pageNumber}
      $left={left}
      $onePager={onePager}
      $turned={turned}
      $width={width || 0}
      $maxWidth={maxWidth || width || 0}
    >
      <StyledPage
        $backwards={backwards}
        $turned={turned}
        $height={height}
        $enhancedScale={enhancedScale}
        $enhancedAvailable={enhancedAvailable}
      >
        <div className="pdf-page" style={{ overflow: 'hidden', background: '#fff', position: 'relative' }}>
          <canvas ref={canvasRef} style={{ display: 'block', background: '#fff' }} />
          {links.length > 0 && (
            <div style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              zIndex: 1, pointerEvents: 'none',
            }}>
              {links.map((link, i) => {
                const { width: pw, height: ph } = pageDimsRef.current;
                if (!pw || !ph) return null;
                const [x0, y0, x1, y1] = link.bounds;
                return (
                  <a
                    key={i}
                    href={link.isExternal ? link.uri : '#'}
                    onClick={e => handleLinkClick(e, link)}
                    style={{
                      position: 'absolute',
                      left: `${(x0 / pw) * 100}%`,
                      top: `${(y0 / ph) * 100}%`,
                      width: `${((x1 - x0) / pw) * 100}%`,
                      height: `${((y1 - y0) / ph) * 100}%`,
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                    }}
                    target={link.isExternal ? '_blank' : undefined}
                    rel={link.isExternal ? 'noopener noreferrer' : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
        {enhance && enhancedScale > 1 && (
          <div className="pdf-page-highres">
            <canvas ref={enhancedCanvasRef} style={{ display: 'block', background: '#fff' }} />
          </div>
        )}
        {enhancedRendering && <RenderingIndicator />}
        {!onePager && (left ? <RotatedPageShadow /> : <PageShadow />)}
      </StyledPage>
    </PageWrapper>
  );
}

// --- Styled components ---

const PageWrapper = styled.div`
  position: absolute;
  transform-origin: ${p => (p.$left ? '100%' : '0')};
  transition: ${p =>
    p.$onePager
      ? 'transform 0.3s ease, opacity 0.3s ease'
      : 'transform 0.15s 0.15s ease-out'};
  transform: none;
  opacity: 1;
  &.turned {
    ${p =>
      p.$onePager
        ? 'transform: translateX(calc(-100% - 50vw)) scale(1.1); opacity: 0;'
        : 'transform: scaleX(0);'}
    ${p => !p.$onePager && 'transition: transform 0.15s ease-in;'}
  }
  --index: ${props => props.$pageNumber};
  z-index: calc(1000 ${p => (p.$left ? '+' : '-')} var(--index));
  width: ${props => props.$width}px;
  max-width: ${props => props.$maxWidth}px;

  ${p => (p.$left ? 'left: 0 !important;' : '')}
`;

const PageShadow = styled.div`
  height: 100%;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.05),
    transparent
  );
  width: 40px;
  z-index: 5000;
  position: absolute;
  top: 0;
  left: 0;
`;

const RotatedPageShadow = styled(PageShadow)`
  && {
    left: auto;
    right: 0;
    transform: rotate(180deg);
  }
`;

const StyledPage = styled.div`
  position: absolute;
  max-width: 100%;
  height: ${props => props.$height}px;
  overflow: visible;
  z-index: ${props => (props.$turned ? '-1' : '1')};
  ${() => !isChrome && 'backface-visibility: hidden; z-index: 1;'}
  & > * {
    overflow: hidden;
  }

  .pdf-page {
    max-height: 100%;
  }

  .pdf-page-highres {
    position: absolute !important;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%)
      scale(${p => 1 / p.$enhancedScale});
    visibility: ${p => (p.$enhancedAvailable ? 'visible' : 'hidden')};
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const RenderingIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 32px;
  height: 32px;
  margin: -16px 0 0 -16px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

const mapStateToProps = state => ({
  onePager: state.magazine.onePager,
  isSearchMode: state.magazine.isSearchMode,
  searchHitsByPage: getSearchHitsByPage(state),
  backwards: state.magazine.backwards,
});

export default connect(mapStateToProps)(Page);
